import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Avatar,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  Collections as CollectionsIcon,
  AccountCircle as AccountCircleIcon,
  Visibility as VisibilityIcon,
  EmojiEvents as EmojiEventsIcon,
  CheckCircle as CheckCircleIcon,
  Loop as LoopIcon,
} from '@mui/icons-material';
import Button from '../UIKit/Button';
import { useTranslation } from 'react-i18next';

const BadgeSeriesProgressDialog = ({
  open,
  onClose,
  badgeSeriesProgress,
  handleViewUserSeriesProgress,
  handleAwardSeriesReward,
  submitLoading,
  getRarityColor,
}) => {
  const { t } = useTranslation(['badges', 'common']);
  if (!badgeSeriesProgress) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <CollectionsIcon sx={{ mr: 1 }} />
          {t('badges:badgeSeriesProgressDialog.title')}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mb={3} display="flex" alignItems="center">
          <Avatar
            src={badgeSeriesProgress.series.image}
            alt={badgeSeriesProgress.series.name}
            sx={{ width: 60, height: 60, mr: 2 }}
          />
          <Box>
            <Typography variant="h6" gutterBottom>
              {badgeSeriesProgress.series.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {badgeSeriesProgress.series.description}
            </Typography>
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            {t('badges:badgeSeriesProgressDialog.badgesInSeries', {
              count: badgeSeriesProgress.series.badges?.length || 0,
            })}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {badgeSeriesProgress.series.badges?.map((badge) => {
              // Получение данных о значке (если badge - это ID или объект)
              const badgeData =
                typeof badge === 'string'
                  ? {
                      _id: badge,
                      name: t('badges:badgeSeriesProgressDialog.badgeDefaultName'),
                      image: '',
                      rarity: 'common',
                      isSecret: false,
                    }
                  : badge;

              return (
                <Tooltip
                  key={typeof badge === 'string' ? badge : badgeData._id}
                  title={badgeData.name}
                >
                  <Avatar
                    src={badgeData.image}
                    alt={badgeData.name}
                    sx={{
                      width: 40,
                      height: 40,
                      border: `2px solid ${getRarityColor(badgeData.rarity)}`,
                      opacity: badgeData.isSecret ? 0.6 : 1,
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        </Box>

        <Typography variant="subtitle1" gutterBottom>
          {t('badges:badgeSeriesProgressDialog.userProgressTitle')}
        </Typography>

        <TableContainer component={Paper} elevation={0} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  {t('badges:badgeSeriesProgressDialog.userColumn')}
                </TableCell>
                <TableCell>
                  {t('badges:badgeSeriesProgressDialog.progressColumn')}
                </TableCell>
                <TableCell>
                  {t('badges:badgeSeriesProgressDialog.collectedBadgesColumn')}
                </TableCell>
                <TableCell>
                  {t('badges:badgeSeriesProgressDialog.statusColumn')}
                </TableCell>
                <TableCell align="center">
                  {t('badges:badgeSeriesProgressDialog.actionsColumn')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {badgeSeriesProgress.userProgress.map((progress) => (
                <TableRow key={progress.userId}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircleIcon sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {progress.username}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        maxWidth: 200,
                      }}
                    >
                      <LinearProgress
                        variant="determinate"
                        value={progress.progress}
                        sx={{
                          width: '100%',
                          height: 8,
                          borderRadius: 5,
                          mr: 1,
                        }}
                        color={progress.isCompleted ? 'success' : 'primary'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(progress.progress)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography variant="body2" mr={1}>
                        {progress.collectedBadges} / {progress.totalBadges}
                      </Typography>
                      {progress.collectedBadgeDetails &&
                        progress.collectedBadgeDetails.length > 0 && (
                          <Tooltip
                            title={t(
                              'badges:badgeSeriesProgressDialog.collectedBadgesTooltip'
                            )}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                              }}
                            >
                              {progress.collectedBadgeDetails.map((badge) => (
                                <Avatar
                                  key={badge._id}
                                  src={badge.image}
                                  alt={badge.name}
                                  sx={{ width: 20, height: 20 }}
                                />
                              ))}
                            </Box>
                          </Tooltip>
                        )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {progress.isCompleted ? (
                      progress.rewardClaimed ? (
                        <Chip
                          label={t('badges:badgeSeriesProgressDialog.rewardClaimed')}
                          color="success"
                          size="small"
                          icon={<CheckCircleIcon />}
                        />
                      ) : (
                        <Chip
                          label={t('badges:badgeSeriesProgressDialog.seriesCompleted')}
                          color="primary"
                          size="small"
                          icon={<EmojiEventsIcon />}
                        />
                      )
                    ) : (
                      <Chip
                        label={t('badges:badgeSeriesProgressDialog.inProgress')}
                        color="default"
                        size="small"
                        icon={<LoopIcon />}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={t('badges:badgeSeriesProgressDialog.detailsTooltip')}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleViewUserSeriesProgress(progress.userId)
                        }
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {progress.isCompleted && !progress.rewardClaimed && (
                      <Tooltip
                        title={t('badges:badgeSeriesProgressDialog.giveRewardTooltip')}
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            handleAwardSeriesReward(
                              progress.userId,
                              badgeSeriesProgress.series._id
                            )
                          }
                          disabled={submitLoading}
                        >
                          {submitLoading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <EmojiEventsIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BadgeSeriesProgressDialog;
